import { Plus } from "lucide-react"

import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import type { ErrorBanner } from "@/lib/api-error-message"

/*
   The engagement table's empty states, split out of the page so it stays composition:
   a failed list, a filtered-out one, and a genuinely empty one. A failed list is not an
   empty one — without the `failed` branch the table falls through to "No engagements
   yet" and invites creating one, which is an answer someone acts on.
*/
export interface ClientsTableEmptyProps {
  failed: boolean
  /** The banner for the failure; only read when `failed` is set. */
  error: ErrorBanner | null
  filtered: boolean
  /** The list query is refetching, so the error's Retry button can show its spinner. */
  retrying: boolean
  onRetry: () => void
  onClearFilters: () => void
  onNewEngagement: () => void
}

export function ClientsTableEmpty({
  failed,
  error,
  filtered,
  retrying,
  onRetry,
  onClearFilters,
  onNewEngagement,
}: ClientsTableEmptyProps): JSX.Element {
  if (failed && error) {
    return (
      <EmptyState
        bare
        reason="error"
        title={error.title}
        body={error.body}
        action={
          <Button size="sm" loading={retrying} onClick={onRetry}>
            Retry
          </Button>
        }
      />
    )
  }

  return (
    <EmptyState
      bare
      reason={filtered ? "filtered" : "empty"}
      title={filtered ? "Nothing matches" : "No engagements yet"}
      body={
        filtered
          ? "Try clearing the search or filters. Reviews can still run — they just are not visible to any client login."
          : "Create one to give a client login access to its own reviews. Reviews can still run without one — they just are not visible to any client login."
      }
      action={
        filtered ? (
          <Button size="sm" onClick={onClearFilters}>
            Clear filters
          </Button>
        ) : (
          <Button variant="primary" size="sm" leadingIcon={<Plus />} onClick={onNewEngagement}>
            New engagement
          </Button>
        )
      }
    />
  )
}
